import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { PageHeader } from './PageHeader';
import { ChatsListPage } from './ChatsListPage';
import './ExpertListPage.css';

interface Expert {
  id: string;
  name: string;
  role: string;
  bio: string;
  tags: string[];
  photo_url: string | null;
  rating: number;
  reviews_count: number;
  is_online: boolean;
  avg_response_time_minutes: number;
  has_existing_chat: boolean;
}

const formatResponseTime = (minutes: number): string => {
  if (minutes < 60) return `~${minutes} мин`;
  const h = Math.round(minutes / 60);
  return `~${h} ч`;
};

export const ExpertListPage: React.FC = () => {
  const navigate = useNavigate();
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(true);
  // Подразделы: каталог экспертов и «Мои чаты» (Соня 27.07).
  const [tab, setTab] = useState<'experts' | 'chats'>('experts');

  useEffect(() => {
    apiClient.getExperts()
      .then((res) => setExperts(res.data ?? []))
      .catch((e) => console.error('Failed to load experts', e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="cbt-experts-page">
      <PageHeader title="Эксперты" />

      <div className="cbt-tabs">
        <button type="button" className={`cbt-tab ${tab === 'experts' ? 'cbt-tab-active' : ''}`} onClick={() => setTab('experts')}>Эксперты</button>
        <button type="button" className={`cbt-tab ${tab === 'chats' ? 'cbt-tab-active' : ''}`} onClick={() => setTab('chats')}>Мои чаты</button>
      </div>

      {tab === 'chats' && <ChatsListPage embedded />}

      {tab === 'experts' && (
      <>
      <div className="cbt-banner">
        <div className="cbt-banner-emoji">🌿</div>
        <div className="cbt-banner-body">
          <p className="cbt-banner-text">
            Хотите получить персональный план медитаций или рассказать про свою жизненную ситуацию,
            задать вопросы по методике расслабления, позам, порядку прослушивания медитаций?
            Напишите нашему эксперту.
          </p>
        </div>
      </div>

      {loading && <div className="cbt-loading">Загружаем экспертов…</div>}

      {!loading && experts.length === 0 && (
        <div className="cbt-loading">Эксперты появятся скоро.</div>
      )}

      <div className="cbt-experts-list">
        {experts.map((e) => (
          <button
            key={e.id}
            className="cbt-expert-card"
            onClick={() => navigate(`/experts/${e.id}/chat`)}
          >
            <div className="cbt-expert-photo">
              {e.photo_url ? (
                <img src={e.photo_url} alt={e.name} />
              ) : (
                <div className="cbt-expert-photo-placeholder">{e.name.charAt(0)}</div>
              )}
              <span className={`cbt-expert-online-dot ${e.is_online ? 'cbt-online' : 'cbt-offline'}`} />
            </div>

            <div className="cbt-expert-body">
              <div className="cbt-expert-row-top">
                <div className="cbt-expert-name">{e.name}</div>
                <div className="cbt-expert-rating">★ {e.rating.toFixed(1)} <span className="cbt-expert-reviews">({e.reviews_count})</span></div>
              </div>
              <div className="cbt-expert-role">{e.role}</div>
              <div className="cbt-expert-bio">{e.bio}</div>
              <div className="cbt-expert-tags">
                {e.tags.map((t) => (<span key={t} className="cbt-expert-tag">{t}</span>))}
              </div>
              <div className="cbt-expert-footer">
                <span className={`cbt-expert-status ${e.is_online ? 'cbt-online' : 'cbt-offline'}`}>
                  {e.is_online ? 'Онлайн' : 'Не в сети'}
                </span>
                <span className="cbt-expert-response">Ответ: {formatResponseTime(e.avg_response_time_minutes)}</span>
                {e.has_existing_chat && <span className="cbt-expert-cta-continue">Продолжить чат →</span>}
                {!e.has_existing_chat && <span className="cbt-expert-cta-start">Написать →</span>}
              </div>
            </div>
          </button>
        ))}
      </div>
      </>
      )}
    </div>
  );
};
